import {
  BofuDocumentRelationship,
  BofuTabDocument,
  DocumentResponse,
  FORM_STATUS_ENUM,
  PersonaStructure,
  PersonFormNodeConfigResponse,
  PersonFormsConfig,
  TabsConfigResponseData,
  TabsConfigResponseDocument,
  TabsConfigResponseForm,
} from "@interfaces/response";
import {
  ALL_FIELDS_DISABLED,
  DEFAULT_PERSON_FORMS_CONFIG,
  DOCUMENT_RELATIONSHIPS_BY_PERSON_TYPE,
  PERSON_FORM_FIELD_CODES,
  PERSON_FORM_HUB_TYPE_CODES,
  PersonFormFieldCode,
  PersonFormFieldConfig,
  PTCAV_EXCLUDED_FIELD_CODES,
} from "@components/PersonForm/personFormConstants";
import {
  generateDocumentConfig,
  generatePropietarioDocs,
  validateDocumentStructure,
} from "@components/TabBofu/functions";
import { BOFU_TABS_CONFIG, BOFU_TABS_FORM_CODES, PERSON_CATALOG } from "@helpers/constantes";
import { PersonaExpediente } from "@store/slices/expedienteSlice";
import { UUID } from "crypto";

export const formatPersonName = (person: PersonaStructure) =>
  `${person.name || ""} ${person.last_name || ""} ${person.last_name_2 || ""}`.trim() || "Sin nombre";

export const getPersonInitials = (person: PersonaStructure) => {
  const first = person.name?.trim()?.charAt(0) || "";
  const last = person.last_name?.trim()?.charAt(0) || "";
  return `${first}${last}`.toUpperCase() || "?";
};

export const isPersonInfoComplete = (status: FORM_STATUS_ENUM | null | undefined) =>
  status === "COMPLETO" || status === "VALIDADO";

/** Posición del código en la jerarquía del hub; -1 si el hub no lo admite. */
const getFormHubTypeIndex = (code: string) => (PERSON_FORM_HUB_TYPE_CODES as readonly string[]).indexOf(code);

/**
 * Menor índice = mayor prioridad. Si tiene varios tipos, usa el de mayor jerarquía;
 * sin tipos del hub regresa `Infinity` para quedar al final.
 */
const getPersonFormHubSortRank = (person: PersonaStructure) => {
  let best = Number.POSITIVE_INFINITY;

  for (const personType of person.person_types_detail || []) {
    const index = getFormHubTypeIndex(personType.code);
    if (index !== -1 && index < best) {
      best = index;
    }
  }

  return best;
};

/** Tipos de la persona que admite el hub TBPER (`PERSON_FORM_HUB_TYPE_CODES`), en su orden original. */
export const getPersonOnlyPersonTypesAllowedToFormHub = (person: PersonaStructure) =>
  (person.person_types_detail || []).filter((personType) => getFormHubTypeIndex(personType.code) >= 0);

/**
 * Deja las personas con algún tipo del hub, con `person_types_detail` reducido a esos tipos,
 * ordenadas por jerarquía. Regresa copias porque los objetos de RTK Query están congelados.
 */
export const filterPersonsForFormHub = (persons: PersonaStructure[]) =>
  persons
    .map((person) => ({ ...person, person_types_detail: getPersonOnlyPersonTypesAllowedToFormHub(person) }))
    .filter((person) => person.person_types_detail.length > 0)
    .sort((a, b) => getPersonFormHubSortRank(a) - getPersonFormHubSortRank(b));

/** Busca la configuración del tab TBPER dentro de la respuesta de `tabs_bofu_config`; `undefined` si no viene. */
export const findPersonasTab = (tabsConfig: TabsConfigResponseData[] | undefined) =>
  (tabsConfig || []).find((tab) => tab.tabs_bofu.code === BOFU_TABS_CONFIG.tab_personas.code);

/**
 * Relaciones de documento que pide la persona según sus tipos (p. ej. PTCAV → `conyuge_aval`).
 * Solo considera tipos definidos en `DOCUMENT_RELATIONSHIPS_BY_PERSON_TYPE`.
 */
export const getPersonDocumentRelationships = (person: PersonaStructure): BofuDocumentRelationship[] =>
  (person.person_types_detail || [])
    .filter((personType) => Object.hasOwn(DOCUMENT_RELATIONSHIPS_BY_PERSON_TYPE, personType.code))
    .flatMap((personType) => DOCUMENT_RELATIONSHIPS_BY_PERSON_TYPE[personType.code] || []);

/**
 * Merge aditivo por `relationship`: genéricos (sin `related_to`) + extras de
 * `conyuge_aval` cuyo `related_to` está en `principalRelationships`
 * (p. ej. `representante_legal`, `obligado_solidario`). Empate de `order`: genérico primero.
 *
 * Para documentos `conyuge_aval` con `matrimonial_regimes_id`: solo se incluyen cuando
 * `principalMatrimonialRegimenId` coincide exactamente con ese id.
 */
export const resolveDocumentsByRelatedTo = (
  tabDocuments: TabsConfigResponseDocument[],
  personRelationships: ReadonlySet<BofuDocumentRelationship>,
  principalRelationships: ReadonlySet<BofuDocumentRelationship> = new Set(),
  principalMatrimonialRegimenId?: string,
): TabsConfigResponseDocument[] => {
  const scoped = tabDocuments.filter(
    (document) =>
      validateDocumentStructure(document) &&
      document.relationship !== undefined &&
      personRelationships.has(document.relationship) &&
      (document.relationship !== "conyuge_aval" ||
        !document.matrimonial_regimes_id ||
        document.matrimonial_regimes_id === principalMatrimonialRegimenId),
  );
  const base = scoped.filter((document) => !document.related_to);
  const extras = scoped.filter(
    (document) =>
      document.relationship === "conyuge_aval" &&
      document.related_to !== undefined &&
      principalRelationships.has(document.related_to),
  );
  const sorted = [...base, ...extras].sort((a, b) => (a.order || 0) - (b.order || 0));
  const seen = new Set<string>();
  return sorted.filter((doc) => {
    if (seen.has(doc.file_category_code)) return false;
    seen.add(doc.file_category_code);
    return true;
  });
};

/**
 * Arma los documentos de una persona según sus tipos, ordenados por `order`, y les asigna
 * los archivos ya cargados de esa persona (mismo file code y `person_id`).
 *
 * Para cónyuge: pasa `allPersons` para resolver `related_to` (documentos adicionales
 * según el tipo del principal que la tiene en `spouse_id`).
 */
export const buildPersonDocuments = ({
  person,
  tabDocuments,
  uploadedDocuments,
  clientDetailsId,
  allPersons = [],
}: {
  person: PersonaStructure;
  tabDocuments: TabsConfigResponseDocument[];
  uploadedDocuments: DocumentResponse[];
  clientDetailsId: UUID;
  /** Personas del hub; necesario para merge de `related_to` en cónyuge. */
  allPersons?: PersonaStructure[];
}): BofuTabDocument[] => {
  const personRelationships = new Set(getPersonDocumentRelationships(person));
  const principalOfPerson = allPersons.find((candidate) => candidate.spouse_id === person.id);
  const principalRelationships = new Set(principalOfPerson ? getPersonDocumentRelationships(principalOfPerson) : []);
  const principalMatrimonialRegimenId = principalOfPerson?.matrimonial_regimes_id || undefined;

  const documents = resolveDocumentsByRelatedTo(
    tabDocuments,
    personRelationships,
    principalRelationships,
    principalMatrimonialRegimenId,
  ).map((docItem) => generateDocumentConfig({ docItem, docs_aval: [], clientDetailsId: undefined, aval: undefined }));

  return generatePropietarioDocs({
    personItem: person,
    valid_documents: documents,
    documents_uploaded: uploadedDocuments,
    clientDetailsId,
  });
};

export type PersonDocumentChange = {
  document: BofuTabDocument;
  newValue: DocumentResponse | undefined;
  valueAction?: "add" | "remove" | "update";
  valueIndex?: number;
};

/** Identifica el mismo documento por file code y relación, para no mezclar tipos de la misma persona. */
const isSameDocumentSlot = (current: BofuTabDocument, target: BofuTabDocument) =>
  current.file_category_code === target.file_category_code && current.relationship === target.relationship;

/**
 * Aplica un cambio sobre los documentos locales de la persona, sin escribir en el store de BOFU.
 * Documento simple: reemplaza `file_value`. Múltiple: agrega, actualiza o quita en `list_values` según `valueAction`.
 * Regresa un arreglo nuevo; los documentos sin cambios conservan su referencia.
 */
export const applyPersonDocumentChange = (
  documents: BofuTabDocument[],
  change: PersonDocumentChange,
): BofuTabDocument[] =>
  documents.map((document) => {
    if (!isSameDocumentSlot(document, change.document)) return document;

    if (!change.document.multiple) {
      return { ...document, file_value: change.newValue };
    }

    const listValues = [...document.list_values];

    if (change.valueAction === "add") {
      listValues.push(change.newValue);
    } else if (change.valueAction === "update" && change.valueIndex !== undefined) {
      listValues[change.valueIndex] = change.newValue;
    } else if (change.valueAction === "remove" && change.valueIndex !== undefined) {
      listValues.splice(change.valueIndex, 1);
    }

    return { ...document, list_values: listValues };
  });

const cloneDisabledFields = (): Record<PersonFormFieldCode, PersonFormFieldConfig> => ({
  ...ALL_FIELDS_DISABLED,
});

/**
 * Combina un campo del default con el del API.
 * Si el API no trae el campo, se usa el default (o deshabilitado si tampoco existe).
 * Si lo trae, `enabled` viene del API y `required` del API o, si no viene, del default.
 */
const mergeFieldConfig = (
  defaultField: PersonFormFieldConfig | undefined,
  apiField: PersonFormFieldConfig | undefined,
): PersonFormFieldConfig => {
  if (!apiField) {
    return defaultField ?? { enabled: false };
  }
  return {
    enabled: apiField.enabled,
    ...(apiField.required !== undefined
      ? { required: apiField.required }
      : defaultField?.required !== undefined
        ? { required: defaultField.required }
        : {}),
  };
};

/**
 * Combina un nodo (tipo de persona) del default con el del API.
 * Sin nodo del API regresa una copia del default. Con nodo: `code`/`label` del API o default,
 * campos mergeados uno a uno, y `relations` del API si vienen (p. ej. PTCAV).
 * `enabled` se conserva por compatibilidad pero el portal lo ignora al resolver campos.
 */
const mergeNodeConfig = (
  defaultNode: PersonFormNodeConfigResponse,
  apiNode: PersonFormNodeConfigResponse | undefined,
): PersonFormNodeConfigResponse => {
  if (!apiNode) {
    return {
      ...defaultNode,
      fields: { ...defaultNode.fields },
      ...(defaultNode.relations ? { relations: structuredClone(defaultNode.relations) } : {}),
    };
  }

  const mergedFields: Record<string, PersonFormFieldConfig> = cloneDisabledFields();

  PERSON_FORM_FIELD_CODES.forEach((fieldCode) => {
    mergedFields[fieldCode] = mergeFieldConfig(defaultNode.fields[fieldCode], apiNode.fields?.[fieldCode]);
  });

  return {
    code: apiNode.code || defaultNode.code,
    label: apiNode.label ?? defaultNode.label,
    enabled: apiNode.enabled ?? defaultNode.enabled,
    fields: mergedFields,
    ...(apiNode.relations
      ? { relations: apiNode.relations }
      : defaultNode.relations
        ? { relations: structuredClone(defaultNode.relations) }
        : {}),
  };
};

/**
 * Obtiene la config de formularios por persona a partir de `tabs_bofu_config`.
 * Sin FORMULARIOS_PERSONA o config vacía → default local.
 * Con config parcial del API → merge sobre el default (nodo y campo).
 * Nodos que solo existen en el API se agregan con sus campos sobre la base deshabilitada.
 */
export const resolvePersonFormsConfig = (forms?: Array<TabsConfigResponseForm>): PersonFormsConfig => {
  const formEntry = forms?.find((form) => form.code === BOFU_TABS_FORM_CODES.formularios_persona);
  const apiConfig = formEntry?.config;

  if (!apiConfig || Object.keys(apiConfig).length === 0) {
    return structuredClone(DEFAULT_PERSON_FORMS_CONFIG);
  }

  const resolved: PersonFormsConfig = {};

  Object.entries(DEFAULT_PERSON_FORMS_CONFIG).forEach(([nodeCode, defaultNode]) => {
    resolved[nodeCode] = mergeNodeConfig(defaultNode, apiConfig[nodeCode]);
  });

  Object.entries(apiConfig).forEach(([nodeCode, apiNode]) => {
    if (!resolved[nodeCode] && apiNode) {
      resolved[nodeCode] = {
        code: apiNode.code || nodeCode,
        label: apiNode.label,
        enabled: apiNode.enabled,
        fields: { ...cloneDisabledFields(), ...(apiNode.fields || {}) },
        ...(apiNode.relations ? { relations: apiNode.relations } : {}),
      };
    }
  });

  return resolved;
};

/**
 * Entre los códigos del principal, elige el de mayor prioridad en el hub que tenga
 * variante en `relations`. `undefined` si ninguno califica.
 */
const pickPrincipalRelationCode = (
  principalTypeCodes: string[],
  relations: Record<string, { fields: Record<string, PersonFormFieldConfig> }> | undefined,
): string | undefined => {
  if (!relations || !principalTypeCodes.length) return undefined;

  let bestCode: string | undefined;
  let bestIndex = Number.POSITIVE_INFINITY;

  for (const code of principalTypeCodes) {
    if (!Object.hasOwn(relations, code)) continue;
    const index = getFormHubTypeIndex(code);
    if (index !== -1 && index < bestIndex) {
      bestIndex = index;
      bestCode = code;
    }
  }

  return bestCode;
};

/**
 * Fields que aporta el nodo PTCAV: `relations[principal]` (reemplazo total) o `PTCAV.fields`.
 */
const resolveSpouseNodeFields = (
  ptcavNode: PersonFormNodeConfigResponse,
  principalTypeCodes: string[] = [],
): Record<string, PersonFormFieldConfig> => {
  const relationCode = pickPrincipalRelationCode(principalTypeCodes, ptcavNode.relations);
  if (relationCode && ptcavNode.relations?.[relationCode]?.fields) {
    return ptcavNode.relations[relationCode].fields;
  }
  return ptcavNode.fields || {};
};

/**
 * Calcula los campos a mostrar/validar para una persona según sus tipos.
 *
 * Capas:
 * 1. `formsConfig` ya debe venir de `resolvePersonFormsConfig` (API mergeado sobre default local por nodo).
 *    Si no se pasa o está vacío, se usa `DEFAULT_PERSON_FORMS_CONFIG`.
 * 2. Unión entre los tipos de la misma persona (p. ej. PTRL + PTAM) con OR:
 *    - `enabled` = true si algún nodo lo tiene enabled
 *    - `required` = true si algún nodo lo marca required
 *    - `node.enabled` se ignora (contrato admin).
 * 3. Para PTCAV: si hay `principalTypeCodes` y existe `relations[codigo]`, esa mapa
 *    reemplaza por completo `PTCAV.fields` como contribución del cónyuge.
 * 4. Si tiene PTCAV, se retiran `civil_status_id`, `matrimonial_regimes_id` y `spouse_id`.
 *
 * Ejemplo PTRL + PTAM (defaults): celular/email/rfc del PTRL + ocupación/estado civil/régimen/cónyuge del PTAM.
 * Codes sin nodo en `formsConfig` se ignoran.
 */
export const resolveFieldsForPerson = (
  personTypeCodes: string[],
  formsConfig?: PersonFormsConfig | null,
  principalTypeCodes: string[] = [],
): Record<PersonFormFieldCode, PersonFormFieldConfig> => {
  if (!personTypeCodes.length) {
    return cloneDisabledFields();
  }

  const config = formsConfig && Object.keys(formsConfig).length > 0 ? formsConfig : DEFAULT_PERSON_FORMS_CONFIG;
  const resolved = cloneDisabledFields();
  const spouseCode = PERSON_CATALOG.conyuge_aval.code;

  const knownTypeCodes = personTypeCodes.filter((typeCode) => Boolean(config[typeCode]));

  knownTypeCodes.forEach((typeCode) => {
    const node = config[typeCode];
    if (!node) {
      return;
    }

    const nodeFields = typeCode === spouseCode ? resolveSpouseNodeFields(node, principalTypeCodes) : node.fields || {};

    PERSON_FORM_FIELD_CODES.forEach((fieldCode) => {
      const field = nodeFields[fieldCode];
      if (!field?.enabled) {
        return;
      }

      resolved[fieldCode] = {
        enabled: true,
        required: Boolean(resolved[fieldCode].required || field.required),
      };
    });
  });

  if (personTypeCodes.includes(spouseCode)) {
    PTCAV_EXCLUDED_FIELD_CODES.forEach((fieldCode) => {
      resolved[fieldCode] = { enabled: false };
    });
  }

  return resolved;
};

/** Indica si hay al menos un campo habilitado para renderizar el formulario. */
export const hasEnabledPersonFields = (fields: Record<PersonFormFieldCode, PersonFormFieldConfig>): boolean =>
  PERSON_FORM_FIELD_CODES.some((code) => fields[code]?.enabled);

/**
 * Filtra y enriquece la lista de personas con las banderas de visibilidad por formulario,
 * derivadas de la configuración del tab TBPER que devuelve el API.
 *
 * Cada bandera indica si la persona debe ver ese sección según sus tipos:
 * - showPersonalForm:       FORMULARIOS_PERSONA → config[typeCode].enabled
 * - showIdentityValidation: VALIDACION_IDENTIDAD → config[typeCode].enabled
 * - showBankForm:           INFORMACION_BANCARIA → config.person_types[typeCode].enabled (nivel extra)
 */
export const enrichPersonsWithFormFlags = (
  persons: PersonaStructure[],
  forms: TabsConfigResponseForm[],
): PersonaExpediente[] => {
  const personalFormConfig = (forms.find((f) => f.code === BOFU_TABS_FORM_CODES.formularios_persona)?.config ||
    {}) as Record<string, { enabled?: boolean }>;

  const identityFormConfig = (forms.find((f) => f.code === BOFU_TABS_FORM_CODES.validacion_identidad)?.config ||
    {}) as Record<string, { enabled?: boolean }>;

  const bankFormPersonTypes =
    (
      forms.find((f) => f.code === BOFU_TABS_FORM_CODES.informacion_bancaria)?.config as {
        person_types?: Record<string, { enabled?: boolean }>;
      }
    )?.person_types || {};

  const buroFormConfig = (forms.find((f) => f.code === BOFU_TABS_FORM_CODES.consulta_buro_persona)?.config ||
    {}) as Record<string, { enabled?: boolean }>;

  return filterPersonsForFormHub(persons).map((person) => ({
    ...person,
    showPersonalForm: { enabled: person.person_types_detail?.some((t) => personalFormConfig[t.code]?.enabled ?? false) ?? false },
    showIdentityValidation: { enabled: person.person_types_detail?.some((t) => identityFormConfig[t.code]?.enabled ?? false) ?? false },
    showBankForm: { enabled: person.person_types_detail?.some((t) => bankFormPersonTypes[t.code]?.enabled ?? false) ?? false },
    showBuroForm: { enabled: person.person_types_detail?.some((t) => buroFormConfig[t.code]?.enabled ?? false) ?? false },
  }));
};

export const computePersonsIsComplete = (
  persons: PersonaExpediente[],
  documentsByPerson: Record<string, BofuTabDocument[]>,
): PersonaExpediente[] =>
  persons.map((person) => {
    const docs = documentsByPerson[person.id] || [];
    const requiredDocs = docs.filter((d) => d.required);
    const allDocsUploaded =
      requiredDocs.length === 0 ||
      requiredDocs.every((d) => (d.multiple ? d.list_values.some(Boolean) : Boolean(d.file_value)));

    const personalFormOk =
      !person.showPersonalForm.enabled ||
      person.personal_information_status === "COMPLETO" ||
      person.personal_information_status === "VALIDADO";

    const identityOk = !person.showIdentityValidation.enabled || person.identity_validation_completed === true;

    const bankFormOk =
      !person.showBankForm.enabled ||
      person.bank_references_status === "COMPLETO" ||
      person.bank_references_status === "VALIDADO";

    const buroOk =
      !person.showBuroForm.enabled ||
      (person.is_credit_bureau_authorization_pf_sended === true &&
        person.is_credit_bureau_authorization_pf_correct === true);

    return { ...person, isComplete: personalFormOk && identityOk && bankFormOk && buroOk && allDocsUploaded };
  });

export const hasPersonType = (
  person: { person_types_detail: Array<{ code: string }> },
  code: string,
): boolean => person.person_types_detail.some((t) => t.code === code);

export const isPFAEPerson = (person: { person_types_detail: Array<{ code: string }> }): boolean =>
  hasPersonType(person, PERSON_CATALOG.representante_legal.code) &&
  hasPersonType(person, PERSON_CATALOG.accionista_mayoritario.code);
